/* eslint-disable no-underscore-dangle */
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { map, take } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from '../../services/storage.service';
import { UserCredentials } from './auth.page';
import { User } from '../../models/user.model';
import { AuthStateService } from './auth-state.service';
import { LoggerService } from '../../services/logger.service';
import { API_BASE_URL } from '../tokens/api.tokens';

const TOKEN_KEY = 'caro-logboek-token';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        null
    );
    token = '';
    _user = new BehaviorSubject<any>(null);

    public currentUser: User | null = null;

    private readonly refreshUrl: string;

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private authStateService: AuthStateService,
        private logger: LoggerService,
        @Inject(API_BASE_URL) private readonly apiBaseUrl: string
    ) {
        this.refreshUrl = `${this.apiBaseUrl}/authentication/refresh-token`;
        this.checkVersion();
        this.loadToken().then(() => {
        });
    }

    public get userId() {
        return this._user.asObservable().pipe(
            map(user => {
                if (user) {
                    return user.id;
                }
                    return null;

            })
        );
    }

    /**
     * Returns the current user's ID from the authenticated state.
     * Uses the validated user data from AuthStateService instead of decoding
     * the JWT token client-side (which would be insecure as it bypasses
     * signature validation).
     */
    public async getActiveUserId(): Promise<number> {
        return this.authStateService.userId;
    }

    public async loadToken() {
        let token = null;
        try {
            token = await this.getActiveUserToken();
        } catch (e) {
            this.logger.error('[AuthService] loadToken() - error', e);
        }
        if (token) {
            this.token = token;
            this.authStateService.setToken(token);

            // Load user profile
            await this.loadCurrentUser();

            const storedUserId = await this.getActiveUserId();
            if (!storedUserId) {
                this.logger.warn('[AuthService] loadToken() - No storedUserId found, logging out');
                this.isAuthenticated.next(false);
                await this.logout();
                return;
            }


            this.isAuthenticated.next(true);
        } else {
            this.isAuthenticated.next(false);
        }
    }

    public async loginUser(credentials: UserCredentials): Promise<string | null> {
        const url = `${this.apiBaseUrl}/authenticate`;
        // const headers = await this.buildHeaders();

        try {
            credentials.username = credentials.username.toLowerCase().trim();

            const result = await this.http.post<any>(url, credentials).toPromise();

            const resultData: any = result.data[0];
            this.token = resultData.accessToken;

            // Store tokens
            await this.storageService.setItem(TOKEN_KEY, this.token);
            await this.storageService.setItem('accessToken', resultData.accessToken as string);
            await this.storageService.setItem('refreshToken', resultData.refreshToken as string);

            // Update AuthStateService
            this.authStateService.setToken(resultData.accessToken);

            // Fetch and store user profile
            await this.loadCurrentUser();

            this.isAuthenticated.next(true);
            return result.toString();
        } catch (error) {
            throw error;
        }
    }

    public async logout(): Promise<void> {
        this.logger.debug('[AuthService] logout() - Clearing user session');
        this.isAuthenticated.next(false);

        // Clear AuthStateService
        this.authStateService.clear();

        // Clear storage
        await this.storageService.clearLocalData(TOKEN_KEY);
        await this.storageService.clearLocalData('accessToken');
        await this.storageService.clearLocalData('refreshToken');
        await this.storageService.clearLocalData('userId');
    }

    public async refreshToken(): Promise<string> {
        const refreshToken: string = await this.storageService.getItem('refreshToken');

        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await this.http
            .post<{ data: any }>(this.refreshUrl, { refreshToken })
            .pipe(take(1))
            .toPromise();

        const resultData: any = response?.data[0];

        if (!resultData?.accessToken) {
            this.logger.error('[AuthService] refreshToken() - No access token in response');
            throw new Error('No valid token received');
        }

        this.token = resultData.accessToken;
        await this.storageService.setItem(TOKEN_KEY, this.token);
        await this.storageService.setItem('accessToken', resultData.accessToken as string);
        await this.storageService.setItem('refreshToken', resultData.refreshToken as string);

        // Update AuthStateService with new token
        this.authStateService.setToken(resultData.accessToken);

        return resultData.accessToken;
    }

    public getUserIsAuthenticated() {
        return this.isAuthenticated;
    }

    public async getActiveUserToken() {
        // Check if getItem is available
        if (!this.storageService || typeof this.storageService.getItem !== 'function') {
            this.logger.warn('[AuthService] StorageService not initialized, initializing...');

            if (this.storageService.init) {
                await this.storageService.init();
            } else {
                throw new Error('StorageService init method missing');
            }
        }

        try {
            const token = await this.storageService.getItem('accessToken');
            return token ? (token as string) : null;
        } catch (error) {
            this.logger.error('[AuthService] Error retrieving access token', error);
            return null;
        }
    }

    public async requestPasswordReset(username: string): Promise<void> {
        try {
            await this.http.post<any>(`${this.apiBaseUrl}/auth/request-password-reset`, {username}).toPromise();
        } catch (error) {
            this.logger.error('[AuthService] requestPasswordReset() - error', error);
        }
    }

    public async resetPassword(token: string, newPassword: string): Promise<void> {
        const urlToUse = `${this.apiBaseUrl}/auth/reset-password`;
        try {
            await this.http.post<{ data: any }>(urlToUse, {token, newPassword}).toPromise();
        } catch (e) {
            // The previous version wrapped the Promise creation in a sync
            // try/catch, which never catches a rejection — failed resets
            // logged nothing here and returned a rejecting promise that the
            // caller sometimes did not handle. Catch + log + re-throw so
            // the calling page's `try/catch` reliably surfaces the error
            // toast/alert to the user.
            this.logger.error('[AuthService] resetPassword() - error', { message: (e as Error).message });
            throw e;
        }
    }

    private async handleRequest<T>(request: Observable<T>): Promise<T> {
        try {
            return await request.toPromise();
        } catch (error) {
            if (error instanceof HttpErrorResponse) {
                this.logger.error('[AuthService] handleRequest() - HTTP error', {
                    status: error.status,
                    statusText: error.statusText
                });
            } else {
                this.logger.error('[AuthService] handleRequest() - error');
            }

            throw error;
        }
    }

    private checkVersion(): void {
    }

    private async loadCurrentUser(): Promise<void> {
        try {
            const user = await this.fetchCurrentUser();
            this.authStateService.setUser(user);

            // Set available shows in AuthStateService
            if (user.shows?.length > 0) {
                this.authStateService.setAvailableShows(user.shows);
            }

            // Pick the active show from the shows array so every flag on the
            // Show model (hasLog, hasPerformerFlyInspections, imageUrl, …)
            // flows through. Cherry-picking individual fields here is how
            // hasPerformerFlyInspections silently went missing after a
            // page reload.
            const requestedActiveShow = user.activeShow?.id
                ? user.shows?.find(show => show.id === user.activeShow.id)
                : undefined;
            const fallbackShow = user.shows?.[0];
            const activeShow = requestedActiveShow ?? fallbackShow;

            if (activeShow) {
                await this.storageService.setItem('showId', activeShow.id);
                this.authStateService.setActiveShow(activeShow);

                if (!requestedActiveShow && user.activeShow?.id) {
                    this.logger.warn('[AuthService] loadCurrentUser() - activeShow not in user.shows, falling back', {
                        invalidActiveShowId: user.activeShow.id,
                        usingShowId: activeShow.id
                    });
                } else if (!requestedActiveShow) {
                    this.logger.debug('[AuthService] loadCurrentUser() - Set active show from first available show', {
                        showId: activeShow.id,
                        showName: activeShow.name
                    });
                }
            } else {
                this.logger.warn('[AuthService] loadCurrentUser() - No activeShow or shows available for user');
            }
        } catch (error) {
            this.logger.error('[AuthService] Failed to load current user', error);
        }
    }

    private async fetchCurrentUser(): Promise<User> {
        const url = `${this.apiBaseUrl}/users/me`;
        const response = await this.http.get<{ data: any }>(url).toPromise();
        return new User().createFromJson(response.data);
    }

    /**
     * Completes the temp-password flow: POSTs the user's chosen new password to
     * /auth/force-change-password. Backend refuses (403) if mustChangePassword
     * isn't set on the user, so this can't be misused as a back-door change-
     * password endpoint by regular logged-in users.
     *
     * <p>After a successful call we re-fetch /me so the in-memory user reflects
     * the now-cleared mustChangePassword flag — otherwise the nav-redirect in
     * app.component would bounce the user straight back to /force-change-password.</p>
     */
    public async forceChangePassword(newPassword: string): Promise<void> {
        const url = `${this.apiBaseUrl}/auth/force-change-password`;
        await this.http.post(url, { newPassword }).toPromise();
        const refreshedUser = await this.fetchCurrentUser();
        this.authStateService.setUser(refreshedUser);
        this.currentUser = refreshedUser;
    }
}

interface LoginResponse {
    idUser?: number;
    token?: string;
}
